'use client';

import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, toast } from '@repo/ui';
import { Fingerprint, Loader2 } from 'lucide-react';
import type { JSX } from 'react';
import { useState, useTransition } from 'react';
import { PasskeyCardSkeleton, PasskeyStatusDisplay } from './helper';
import { RemovePasskeyDialog } from './remove-passkey-dialog';
import { usePasskeyStatus } from './use-passkey-status';

async function handleAddPasskeyAction(addPasskey: () => Promise<void>): Promise<void> {
  try {
    await addPasskey();
    toast.success('Passkey added successfully');
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to add passkey';
    const isCancelled = message.includes('cancelled') || message.includes('canceled');
    if (!isCancelled) {
      toast.error(message);
    }
  }
}

export function PasskeyStatusCard(): JSX.Element {
  const { hasPasskey, isLoading, error, addPasskey, removePasskey } = usePasskeyStatus();
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [isPending, startTransition] = useTransition();

  if (isLoading) {
    return <PasskeyCardSkeleton />;
  }

  const handleAddPasskey = (): void => {
    startTransition(() => handleAddPasskeyAction(addPasskey));
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            <CardTitle>Passkey</CardTitle>
          </div>
          <CardDescription>Use biometrics like Face ID or Touch ID to sign in</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">{error}</div>
          )}
          <div className="flex items-center justify-between">
            <PasskeyStatusDisplay hasPasskey={hasPasskey} />
            {hasPasskey ? (
              <Button variant="outline" size="sm" onClick={() => setRemoveDialogOpen(true)}>
                Remove
              </Button>
            ) : (
              <Button size="sm" onClick={handleAddPasskey} disabled={isPending}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Set up passkey
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      <RemovePasskeyDialog
        open={removeDialogOpen}
        onOpenChange={setRemoveDialogOpen}
        onRemove={removePasskey}
      />
    </>
  );
}
